/**
 * Real-time Notification Component
 * 
 * Displays toast notifications for real-time events:
 * - Item edits
 * - New comments
 * - @mentions
 * - Lock changes
 * - Value updates
 * - Duplicate detection
 * - File uploads
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTimes,
  faBell,
  faEdit,
  faComment,
  faAt,
  faLock,
  faDollarSign,
  faCopy,
  faFile,
  faExternalLinkAlt,
  faTrash,
  faCheckDouble,
  faEnvelope,
  faEnvelopeOpen,
} from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import '../styles/NotificationToast.css';
import useI18n from '../hooks/useI18n';

interface Notification {
  id: string;
  type: string;
  title: string;
  message: string;
  relatedItemId?: string;
  relatedUserId?: string;
  actionUrl?: string;
  createdAt: string;
  isRead: boolean;
}

interface NotificationToastProps {
  notification: Notification;
  onClose: () => void;
  onAction?: () => void;
  autoHideDelay?: number;
}

const getNotificationIcon = (type: string) => {
  switch (type) {
    case 'ITEM_UPDATE':
      return faEdit;
    case 'COMMENT':
      return faComment;
    case 'MENTION':
      return faAt;
    case 'LOCK_CHANGE':
      return faLock;
    case 'AI_SUGGESTION':
    case 'VALUE_CHANGED':
      return faDollarSign;
    case 'DUPLICATE_DETECTED':
      return faCopy;
    case 'FILE_UPLOAD':
      return faFile;
    default:
      return faBell;
  }
};

const getNotificationColor = (type: string) => {
  switch (type) {
    case 'ITEM_UPDATE':
      return '#3b82f6';
    case 'COMMENT':
      return '#10b981';
    case 'MENTION':
      return '#8b5cf6';
    case 'LOCK_CHANGE':
      return '#f59e0b';
    case 'AI_SUGGESTION':
    case 'VALUE_CHANGED':
      return '#06b6d4';
    case 'DUPLICATE_DETECTED':
      return '#ec4899';
    case 'FILE_UPLOAD':
      return '#6366f1';
    default:
      return '#64748b';
  }
};

const getNotificationActionUrl = (notification: Notification) => {
  if (notification.actionUrl) return notification.actionUrl;
  if (!notification.relatedItemId) return undefined;

  switch (notification.type) {
    case 'COMMENT':
    case 'MENTION':
      return `/library?item=${notification.relatedItemId}&tab=comments`;
    case 'AI_SUGGESTION':
    case 'DUPLICATE_DETECTED':
    case 'VALUE_CHANGED':
      return `/library?item=${notification.relatedItemId}&tab=details`;
    case 'ITEM_UPDATE':
    default:
      return `/library?item=${notification.relatedItemId}`;
  }
};

/**
 * Individual Toast Notification
 */
export const NotificationToast: React.FC<NotificationToastProps> = ({
  notification,
  onClose,
  onAction,
  autoHideDelay = 5000,
}) => {
  const [isExiting, setIsExiting] = useState(false);
  const timerRef = useRef<NodeJS.Timeout>();
  const navigate = useNavigate();
  const { t } = useI18n();

  const handleClose = useCallback(() => {
    setIsExiting(true);
    setTimeout(() => {
      onClose();
    }, 300);
  }, [onClose]);

  useEffect(() => {
    if (autoHideDelay > 0) {
      timerRef.current = setTimeout(() => {
        handleClose();
      }, autoHideDelay);
    }

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [autoHideDelay, handleClose]);

  const handleClick = useCallback(() => {
    const actionUrl = getNotificationActionUrl(notification);
    if (actionUrl) {
      navigate(actionUrl);
    }
    if (onAction) {
      onAction();
    }
    handleClose();
  }, [notification, onAction, handleClose, navigate]);

  const handleMouseEnter = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
  };

  const handleMouseLeave = () => {
    if (autoHideDelay > 0) {
      timerRef.current = setTimeout(() => {
        handleClose();
      }, 2000);
    }
  };

  const icon = getNotificationIcon(notification.type);
  const color = getNotificationColor(notification.type);

  return (
    <div
      className={`notification-toast ${isExiting ? 'exiting' : ''}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      style={{ '--accent-color': color } as React.CSSProperties}
    >
      <div className="toast-icon" style={{ backgroundColor: color }}>
        <FontAwesomeIcon icon={icon} />
      </div>
      <div className="toast-content" onClick={handleClick}>
        <div className="toast-title">{notification.title}</div>
        <div className="toast-message">{notification.message}</div>
        {getNotificationActionUrl(notification) && (
          <div className="toast-action">
            <span>{t('notifications.clickToView')}</span>
            <FontAwesomeIcon icon={faExternalLinkAlt} />
          </div>
        )}
      </div>
      <button className="toast-close" onClick={handleClose}>
        <FontAwesomeIcon icon={faTimes} />
      </button>
    </div>
  );
};

/**
 * Notification Toast Container
 */
interface NotificationToastContainerProps {
  notifications: Notification[];
  onDismiss: (id: string) => void;
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left';
  maxVisible?: number;
}

export const NotificationToastContainer: React.FC<NotificationToastContainerProps> = ({
  notifications,
  onDismiss,
  position = 'top-right',
  maxVisible = 5,
}) => {
  const { t } = useI18n();
  const visibleNotifications = notifications.slice(0, maxVisible);

  return (
    <div className={`notification-toast-container ${position}`}>
      {visibleNotifications.map((notification) => (
        <NotificationToast
          key={notification.id}
          notification={notification}
          onClose={() => onDismiss(notification.id)}
        />
      ))}
      {notifications.length > maxVisible && (
        <div className="toast-overflow">
          {t('notifications.moreNotifications').replace('{count}', String(notifications.length - maxVisible))}
        </div>
      )}
    </div>
  );
};

/**
 * Notification Center Dropdown
 */
interface NotificationCenterProps {
  notifications: Notification[];
  unreadCount: number;
  onMarkAsRead: (id: string) => void;
  onMarkAsUnread: (id: string) => void;
  onMarkAllAsRead: () => void;
  onClear: (id: string) => void;
  onClearAll: () => void;
  onNavigate: (url: string) => void;
  showLabel?: boolean;
}

export const NotificationCenter: React.FC<NotificationCenterProps> = ({
  notifications,
  unreadCount,
  onMarkAsRead,
  onMarkAsUnread,
  onMarkAllAsRead,
  onClear,
  onClearAll,
  onNavigate,
  showLabel = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [filter, setFilter] = useState<'all' | 'unread'>('all');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const dropdownPanelRef = useRef<HTMLDivElement>(null);
  const bellButtonRef = useRef<HTMLButtonElement>(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 72, right: 16, width: 380 });
  const { t } = useI18n();

  const updateDropdownPosition = useCallback(() => {
    if (!bellButtonRef.current) return;

    const rect = bellButtonRef.current.getBoundingClientRect();
    const viewportPadding = 12;
    const maxWidth = Math.max(280, Math.min(380, window.innerWidth - viewportPadding * 2));

    setDropdownPosition({
      top: Math.max(8, rect.bottom + 8),
      right: Math.max(viewportPadding, window.innerWidth - rect.right),
      width: maxWidth,
    });
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      const clickedBellRegion = dropdownRef.current?.contains(target);
      const clickedDropdownPanel = dropdownPanelRef.current?.contains(target);

      if (!clickedBellRegion && !clickedDropdownPanel) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    updateDropdownPosition();
    const reposition = () => updateDropdownPosition();
    window.addEventListener('resize', reposition);
    window.addEventListener('scroll', reposition, true);

    return () => {
      window.removeEventListener('resize', reposition);
      window.removeEventListener('scroll', reposition, true);
    };
  }, [isOpen, updateDropdownPosition]);

  const filteredNotifications =
    filter === 'unread'
      ? notifications.filter((n) => !n.isRead)
      : notifications;

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.isRead) {
      onMarkAsRead(notification.id);
    }
    const actionUrl = getNotificationActionUrl(notification);
    if (actionUrl) {
      onNavigate(actionUrl);
    }
    setIsOpen(false);
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return t('notifications.justNow');
    if (diffMins < 60) return t('notifications.minutesAgo').replace('{count}', String(diffMins));
    if (diffHours < 24) return t('notifications.hoursAgo').replace('{count}', String(diffHours));
    if (diffDays < 7) return t('notifications.daysAgo').replace('{count}', String(diffDays));
    return date.toLocaleDateString();
  };

  return (
    <div className="notification-center" ref={dropdownRef}>
      <button
        ref={bellButtonRef}
        className="notification-bell"
        onClick={() => {
          if (!isOpen) updateDropdownPosition();
          setIsOpen(!isOpen);
        }}
        title={t('notifications.title')}
        aria-label={unreadCount > 0 ? t('notifications.title') + ', ' + unreadCount + ' unread' : t('notifications.title')}
      >
        <FontAwesomeIcon icon={faBell} />
        {showLabel && <span className="notification-bell-label">Notifications</span>}
        {unreadCount > 0 && (
          <span className="notification-badge">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && createPortal(
        <div
          className="notification-dropdown"
          ref={dropdownPanelRef}
          style={{
            top: `${dropdownPosition.top}px`,
            right: `${dropdownPosition.right}px`,
            width: `min(${dropdownPosition.width}px, calc(100vw - 24px))`,
          }}
        >
          <div className="notification-header">
            <h3>{t('notifications.title')}</h3>
            <div className="notification-filters">
              <button
                className={filter === 'all' ? 'active' : ''}
                onClick={() => setFilter('all')}
              >
                {t('notifications.all')}
              </button>
              <button
                className={filter === 'unread' ? 'active' : ''}
                onClick={() => setFilter('unread')}
              >
                {t('notifications.unread')} ({unreadCount})
              </button>
            </div>
          </div>

          <div className="notification-actions-bar">
            <button onClick={onMarkAllAsRead} disabled={unreadCount === 0}>
              <FontAwesomeIcon icon={faCheckDouble} />
              {t('notifications.markAllRead')}
            </button>
            <button onClick={onClearAll} disabled={notifications.length === 0}>
              <FontAwesomeIcon icon={faTrash} />
              {t('notifications.clearAll')}
            </button>
          </div>

          <div className="notification-list">
            {filteredNotifications.length === 0 ? (
              <div className="notification-empty">
                <FontAwesomeIcon icon={faBell} />
                <p>{t('notifications.empty')}</p>
              </div>
            ) : (
              filteredNotifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`notification-item ${notification.isRead ? '' : 'unread'}`}
                  onClick={() => handleNotificationClick(notification)}
                >
                  <div
                    className="notification-icon"
                    style={{
                      backgroundColor: getNotificationColor(notification.type),
                    }}
                  >
                    <FontAwesomeIcon icon={getNotificationIcon(notification.type)} />
                  </div>
                  <div className="notification-content">
                    <div className="notification-title">{notification.title}</div>
                    <div className="notification-message">{notification.message}</div>
                    <div className="notification-time">
                      {formatTime(notification.createdAt)}
                    </div>
                  </div>
                  <button
                    className="notification-read-toggle"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (notification.isRead) {
                        onMarkAsUnread(notification.id);
                      } else {
                        onMarkAsRead(notification.id);
                      }
                    }}
                    title={notification.isRead 
                      ? (t('notifications.markUnread') || 'Mark as unread')
                      : (t('notifications.markRead') || 'Mark as read')
                    }
                  >
                    <FontAwesomeIcon icon={notification.isRead ? faEnvelope : faEnvelopeOpen} />
                  </button>
                  <button
                    className="notification-dismiss"
                    onClick={(e) => {
                      e.stopPropagation();
                      onClear(notification.id);
                    }}
                    title={t('notifications.delete') || 'Delete'}
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                </div>
              ))
            )}
          </div>

          {notifications.length > 10 && (
            <div className="notification-footer">
              <button onClick={() => onNavigate('/notifications')}>
                {t('notifications.viewAll')}
              </button>
            </div>
          )}
        </div>,
        document.body
      )}
    </div>
  );
};

export default NotificationCenter;
